import { COLORS, paddingSm, paddingXs } from 'assets/styled';
import { respondTo } from 'assets/styled/breakpoints';
import styled, { css } from 'styled-components';
import { ADDITIONAL_WRAPPER_MIN_HEIGHT } from '../FormItemWrapper/FormItemWrapper.styled';

interface StyledProps {
  hasValue: boolean;
  showBorder: boolean;
  hasPrice: boolean;
  opacityIndex: number;
  opacityCoefficient: number;
  hasRedStar?: boolean;
  isSkeleton?: boolean;
  disabled?: boolean;
  needsResolving?: boolean;
  isInlineControl?: boolean;
  stacksWhenNarrow?: boolean;
}

// Height of the shared line a question name and an inline control sit on. Set
// to the tallest of them, MUI's radio at ~44px: the name and the control each
// centre inside this height, so they stay aligned even though a toggle is
// shorter than a radio and drops to MUI's small size on phones.
//
// The name is given the height rather than being centred against the control
// so that an error or an added price rendering underneath cannot drag it off
// the control's line.
export const INLINE_CONTROL_MIN_HEIGHT = 49;

export const QuestionName = styled.label`
  max-width: 100%;
  font-size: 17rem;
  font-weight: 400;
  color: ${COLORS.BLACK};
  transition: 0.3s ease;

  ${respondTo.bigPhone`
    font-size: 16rem;
  `};
`;

export const FormItemContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${paddingSm} ${paddingXs} 5rem;
  transition: 0.3s ease-in-out;
  max-width: 100%;
  width: 100%;

  ${(props: StyledProps) =>
    props.hasValue &&
    css`
      color: ${COLORS.GREEN};
      background: ${COLORS.BACKGROUND};
    `};

  ${(props: StyledProps) =>
    props.disabled &&
    css`
      cursor: not-allowed;
      background: ${COLORS.GRAY_300};
      opacity: 0.9;
      color: ${COLORS.BLACK};
      & * {
        pointer-events: none;
      }
    `};

  ${(props: StyledProps) =>
    props.needsResolving &&
    css`
      border: 1rem solid ${COLORS.RED_100};
    `};

  ${respondTo.smallTablet`
      flex-wrap: wrap;
      gap: 5rem;
      padding: 15rem ${paddingXs} 5rem;
    `};

  // A radio group or a toggle is narrow enough to sit beside the question name
  // even on a phone, so the shared line is kept at every width; if a long name
  // leaves it no room it wraps and stays right aligned.
  //
  // && doubles the class for specificity. styled-components emits nested media
  // blocks after the flat declarations, so the responsive rules above would
  // otherwise win on narrow screens no matter where this block is declared.
  ${(props: StyledProps) =>
    props.isInlineControl &&
    css`
      && {
        flex-direction: row;
        align-items: flex-start;
        flex-wrap: wrap;
        column-gap: 15rem;
        row-gap: 0;
        padding-top: 12rem;
      }

      ${Column} {
        flex: 1 1 auto;
        min-width: 0;
      }

      ${QuestionNameAsteriskInfoRow} {
        margin-bottom: 0;
        min-height: ${INLINE_CONTROL_MIN_HEIGHT}rem;
      }

      // Quotation mode's Indicative checkbox is a third child of this row, so
      // it would otherwise sit beside the control instead of under it the way
      // it does for every other question type. A full basis wraps it onto its
      // own line; spanning the row leaves margin-left: auto nothing to push
      // against, so the contents are right aligned here instead.
      ${CheckboxWrapper} {
        flex-basis: 100%;
        width: 100%;
        display: flex;
        justify-content: flex-end;
      }

      // Once the control takes its own line there is nothing left to align the
      // name against, so it drops back to its natural height.
      ${(inner: StyledProps) =>
        inner.stacksWhenNarrow &&
        css`
          ${respondTo.smallTablet`
            row-gap: 5rem;

            ${QuestionNameAsteriskInfoRow} {
              min-height: 0;
            }
          `};
        `};
    `};
`;

export const RedAsteriskWrapper = styled.label`
  position: relative;
  padding-left: 5rem;
  pointer-events: none;
  background-color: red;
`;

export const RedAsterisk = styled.label`
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  position: absolute;
  font-weight: 400;
  font-size: 30rem;
  color: ${COLORS.RED_100};
  pointer-events: none;
`;

export const QuestionNameAsteriskInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10rem;
  margin-bottom: 10rem;
`;

export const TooltipWrapper = styled.div`
  margin-left: 5rem;
`;

export const ContainerGrayBottomBorder = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  ${(props: StyledProps) =>
    !props.disabled &&
    css`
      :hover {
        ${FormItemContainer} {
          color: ${COLORS.GREEN};
          background: ${COLORS.BACKGROUND};
        }

        ${QuestionName} {
          color: ${COLORS.GREEN};
        }
      }
    `};

  ${(props: StyledProps) =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `};

  ${(props: StyledProps) =>
    props.showBorder &&
    css`
      border-bottom: 1px solid ${COLORS.BORDER};
      border-width: thin;
    `};
  ${(props: StyledProps) =>
    props.opacityIndex !== undefined &&
    css`
      opacity: ${1 - (props.opacityIndex * props.opacityCoefficient + 0.2)};
    `};
  ${(props: StyledProps) =>
    props.isSkeleton &&
    css`
      padding-bottom: 24rem;
    `}
`;

export const AdditionalMargin = styled.div`
  // FormItemWrapper's AdditionalWrapper causes the question name to be off center,
  // this margin moves the question name towards the center of the FormItemContainer
  min-height: ${ADDITIONAL_WRAPPER_MIN_HEIGHT}rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${respondTo.smallTablet`
      display: none;
  `};
`;

export const Column = styled.div`
  // Used for positioning AdditionalMargin below the question name
  display: flex;
  flex-direction: column;
`;

export const CheckboxWrapper = styled.div`
  margin-top: 6rem;
  margin-bottom: 6rem;
  margin-left: auto;
`;
